"use client";

import React from "react";
import { Anchor, CircleCheck, ShieldCheck, Ship, Loader2, Info } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
const requiredInfo = [{
  icon: Ship,
  text: "Vessel Name & IMO Number"
}, {
  icon: Anchor,
  text: "Current Location / Port of Call"
}, {
  icon: ShieldCheck,
  text: "Specific Supply Categories"
}, {
  icon: CircleCheck,
  text: "Delivery Timeline"
}];
const supplyOptions = ["Provisions", "Spare Parts", "Cleaning Services", "Technical Services", "Other"];
const deliveryLocationOptions = ["Berth / Terminal", "Anchorage", "Offshore", "Shipyard"];
const deliveryMethodOptions = ["Ship's Crane", "Port Crane", "Launch Boat Delivery", "Shore Delivery"];
function QuoteFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-sm border border-border bg-card p-12 text-center shadow-lg">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-ring">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground">Request Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Send Another Request
        </Button>
      </div>;
  }
  const inputClasses = "flex w-full rounded-sm border border-primary/20 bg-primary px-4 py-3 text-sm text-primary-foreground placeholder:text-primary-foreground/40 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-all disabled:cursor-not-allowed disabled:opacity-50 h-auto";
  return <Form onSubmit={handleSubmit} className="space-y-8 rounded-sm border border-border bg-card p-6 shadow-xl sm:p-10">
      <div className="space-y-6">
        <div className="border-b border-border pb-4">
          <h3 className="text-lg font-serif font-semibold text-foreground">1. Company Details</h3>
        </div>
        
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="company_name" rules={{
          required: "Company name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Company Name *</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="Oceanic Shipping Ltd." />
                </FormControl>
                <FormDescription>Name of the vessel operating company.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="contact_name" rules={{
          required: "Contact person is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Contact Person *</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="Maria González" />
                </FormControl>
                <FormDescription>Full name of the person handling the request.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Email Address *</FormLabel>
                <FormControl>
                  <Input {...field} type="email" className={inputClasses} placeholder="maria.gonzalez@example.com" />
                </FormControl>
                <FormDescription>We'll send the quote to this address.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Phone Number</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" className={inputClasses} placeholder="+1 (555) 123‑14 567" />
                </FormControl>
                <FormDescription>Optional – for quick follow‑up.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="space-y-6 pt-4">
        <div className="border-b border-border pb-4">
          <h3 className="text-lg font-serif font-semibold text-foreground">2. Vessel & Schedule</h3>
        </div>

        <FormField control={control} name="vessel_details" rules={{
        required: "Vessel details are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Vessel Details *</FormLabel>
              <FormControl>
                <Textarea {...field} className={cn(inputClasses, "min-h-[100px] resize-y")} placeholder="Vessel name, IMO number, flag, current location" />
              </FormControl>
              <FormDescription>Provide any identifiers that help us match the right supplies.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="eta" rules={{
          required: "ETA is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">ETA (Estimated Time of Arrival) *</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="e.g., 2026-04-15 14:00 UTC" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="etd" rules={{
          required: "ETD is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">ETD (Estimated Time of Departure) *</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="e.g., 2026-04-20 08:00 UTC" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="next_port_of_call" rules={{
          required: "Next port of call is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Next Port of Call *</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="e.g., Port of Panama" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="second_port_of_call" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Second Port of Call</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="e.g., Port of Cartagena" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="space-y-6 pt-4">
        <div className="border-b border-border pb-4">
          <h3 className="text-lg font-serif font-semibold text-foreground">3. Agent Details</h3>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="agent_company_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Agent Company Name</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="e.g., Global Shipping Agents Ltd." />
                </FormControl>
                <FormDescription>Name of the agent representing the vessel.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agent_contact_person" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Contact Person</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClasses} placeholder="John Doe" />
                </FormControl>
                <FormDescription>Agent's contact person.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="agent_email" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Email Address</FormLabel>
                <FormControl>
                  <Input {...field} type="email" className={inputClasses} placeholder="agent@example.com" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agent_phone_international" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">International Phone Number</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" className={inputClasses} placeholder="+1 (555) 123‑14 567" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="agent_phone_local" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Local Phone Number</FormLabel>
              <FormControl>
                <Input {...field} type="tel" className={inputClasses} placeholder="555‑1234" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="space-y-6 pt-4">
        <div className="border-b border-border pb-4">
          <h3 className="text-lg font-serif font-semibold text-foreground">4. Delivery & Logistics</h3>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField control={control} name="delivery_location" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Delivery Location</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={inputClasses}>
                      <SelectValue placeholder="Select delivery location" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {deliveryLocationOptions.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="delivery_method" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Delivery Method</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={inputClasses}>
                      <SelectValue placeholder="Select delivery method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {deliveryMethodOptions.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="required_certificates" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Required Certificates (if applicable)</FormLabel>
              <FormControl>
                <Textarea {...field} className={cn(inputClasses, "min-h-[100px] resize-y")} placeholder="e.g., COA, Class certificates" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="space-y-6 pt-4">
        <div className="border-b border-border pb-4">
          <h3 className="text-lg font-serif font-semibold text-foreground">5. Supply Requirements</h3>
        </div>

        <FormField control={control} name="supply_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Supply Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className={inputClasses}>
                    <SelectValue placeholder="Select a supply category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {supplyOptions.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_details" rules={{
        required: "Request details are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Request details *</FormLabel>
              <FormControl>
                <Textarea {...field} className={cn(inputClasses, "min-h-[100px] resize-y")} placeholder="Specify quantities, preferred brands, delivery timeline, etc." />
              </FormControl>
              <FormDescription>Any extra information to help us prepare an accurate quote.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="supporting_documents" render={({
        field: {
          value,
          onChange,
          ...fieldProps
        }
      }) => <FormItem>
              <FormLabel className="text-foreground">Supporting Documents (Optional)</FormLabel>
              <FormControl>
                <Input {...fieldProps} type="file" className={cn(inputClasses, "cursor-pointer pt-2.5")} onChange={e => onChange(e.target.files?.[0])} />
              </FormControl>
              <FormDescription>
                Upload requisition lists, spare part photos, technical drawings, specification sheets, or any supporting documents related to your request.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consent_processing" rules={{
      required: "You must consent to the processing of your data to submit this request."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-sm border border-border/50 bg-muted/20 p-4">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 shrink-0 rounded-sm border border-primary/30 bg-primary/10 accent-primary focus:ring-1 focus:ring-ring" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-muted-foreground leading-relaxed">
                I consent to the processing of my personal data for the purpose of responding to my service inquiry, in accordance with the <Link to="/privacy-policy" newTab className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">Privacy Policy</Link>.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      <div className="flex items-start gap-4 rounded-sm border border-border/50 bg-muted/30 p-4">
        <Info className="mt-1 size-5 shrink-0 text-muted-foreground" />
        <p className="text-sm leading-relaxed text-muted-foreground italic">
          If some information is not yet available, please submit the request with the details currently known. Our team will follow up for any missing information.
        </p>
      </div>

      {submitError && <div className="rounded-sm border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-sm bg-primary text-lg font-medium text-primary-foreground hover:bg-primary/90">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Submit Quote Request"}
      </Button>
    </Form>;
}
export default function RequestSupplyQuote() {
  return <section id="request-quote-home" className="bg-background py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-8">
          
          <div className="flex flex-col justify-center lg:col-span-5">
            <div className="mb-8 inline-flex items-center rounded-full border border-border bg-muted/50 px-3 py-1 text-sm font-medium text-muted-foreground">
              <span className="mr-2 flex size-2 rounded-full bg-ring"></span>
              Global Maritime Brokerage
            </div>
            <h2 className="mb-6 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Request Supply Quote
            </h2>
            <p className="mb-10 text-lg leading-relaxed text-muted-foreground">
              <span className="block mb-4 text-foreground/80 font-medium">
                Submit your vessel requirements and receive coordinated supplier quotations within 24 hours.
              </span>
              Streamline your vessel's supply chain through our professional maritime brokerage services. We connect vessels with trusted maritime suppliers and technical service providers through a global network. Provide your requirements below, and our team will handle the supply coordination with our global maritime partners to deliver a competitive, comprehensive quote.
            </p>
            
            <div className="space-y-6 rounded-sm border border-border bg-card/50 p-8">
              <h4 className="font-serif text-lg font-semibold text-foreground">
                Required Details for Brokerage Coordination
              </h4>
              <ul className="space-y-4">
                {requiredInfo.map((item, i) => {
                const Icon = item.icon;
                return <li key={i} data-index={i} className="flex items-center text-muted-foreground">
                      <div className="mr-4 flex size-10 shrink-0 items-center justify-center rounded-sm bg-primary/5 text-primary">
                        <Icon className="size-5" />
                      </div>
                      <span className="font-medium">{item.text}</span>
                    </li>;
              })}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formId="request_supply_quote_home" sectionName="Request Supply Quote" defaultValues={{
            company_name: "",
            contact_name: "",
            email: "",
            phone: "",
            vessel_details: "",
            eta: "",
            etd: "",
            next_port_of_call: "",
            second_port_of_call: "",
            agent_company_name: "",
            agent_contact_person: "",
            agent_email: "",
            agent_phone_international: "",
            agent_phone_local: "",
            delivery_location: "",
            delivery_method: "",
            required_certificates: "",
            supply_type: "",
            additional_details: "",
            supporting_documents: "",
            consent_processing: false,
            submission_help_note: ""
          }} validationRules={{
            company_name: {
              required: true
            },
            contact_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            vessel_details: {
              required: true
            },
            eta: {
              required: true
            },
            etd: {
              required: true
            },
            next_port_of_call: {
              required: true
            },
            additional_details: {
              required: true
            },
            consent_processing: {
              required: true
            }
          }} submitText="Submit Quote Request" successMessage="Thank you. Your supply quote request has been securely transmitted to our brokerage coordination team for sourcing and quotation coordination. We will review your vessel details and contact you shortly.">
              <QuoteFormInner />
            </FormProvider>
          </div>
          
        </div>
      </div>
    </section>;
}